'use client'

/**
 * Who this answer is about, above the answer (#16822, PRD R1.9).
 *
 * "About Michael Chen, 7th grade · Riverbend Middle" — so the asker knows,
 * before reading a word of the answer, which of several same-named people
 * it describes. When another in-scope person matched the same name, "Not
 * this one? Switch" opens the other matches as one-click choices
 * (`./entity-choices`); choosing records the switch on the turn's row and
 * asks the question again about them. With one match in scope there is no
 * switch, and nothing hints that anyone else by that name exists.
 *
 * The toggle is a native button with `aria-expanded`, so the switch is
 * reachable and operable by keyboard. Plain text, semantic tokens.
 */

import { useState } from 'react'

import { ArrowLeftRight, UserCheck } from '@/components/icons'
import type { ResolvedPresentation } from '@/lib/ai-chat/entity-presentation'
import type { EntityPin } from '@/lib/ai-chat/resolve-entities'

import { EntityChoices } from './entity-choices'

export function ResolvedBar({
  resolved,
  onAsk,
  disabled = false,
}: {
  resolved: ResolvedPresentation
  /** Ask a question as the asker's next turn, about the person chosen. */
  onAsk: (question: string, entityPick?: EntityPin) => void
  /** True while a turn is streaming. */
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const { switchTo } = resolved

  return (
    <div
      data-testid="ai-chat-resolved-bar"
      className="mb-2 rounded-md bg-surface-panel px-3 py-2"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="flex min-w-0 items-center gap-1.5 text-sm text-text-primary">
          <UserCheck
            className="h-3.5 w-3.5 shrink-0 text-text-secondary"
            aria-hidden
          />
          <span className="min-w-0">{`About ${resolved.label}`}</span>
        </p>
        {switchTo && (
          <button
            type="button"
            data-testid="ai-chat-resolved-switch"
            aria-expanded={open}
            disabled={disabled}
            onClick={() => setOpen(current => !current)}
            className="inline-flex items-center gap-1 rounded-sm text-xs text-text-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stroke-selected disabled:cursor-not-allowed disabled:opacity-60 pointer-coarse:min-h-11"
          >
            <ArrowLeftRight className="h-3 w-3 shrink-0" aria-hidden />
            Not this one? Switch
          </button>
        )}
      </div>
      {switchTo && open && (
        <EntityChoices step={switchTo} onAsk={onAsk} disabled={disabled} />
      )}
    </div>
  )
}
